import { track, cameFrom, screenScrollPct } from './growthTracker';

// Growth OS step 3 — the store's events (report section B). Event names and
// keys must match growth_event_defs; anything else is dropped by the server.
// Every function here is fire-and-forget: tracking never breaks a page.

const UUID_PATH = /^\/market\/[0-9a-f]{8}-[0-9a-f]{4}-/i;

// Where a product page was reached from, read off the previous screen.
export function productSource(from = cameFrom()) {
  if (!from) return 'link';
  if (from.startsWith('/market/search') || from.startsWith('/market/products')) return 'search';
  if (from.startsWith('/market/c/')) return 'category';
  if (from.startsWith('/market/image-search')) return 'image';
  if (from === '/' || from === '/market') return 'home';
  if (from.startsWith('/market/cart')) return 'cart';
  if (UUID_PATH.test(from)) return 'other_product';
  return 'other';
}

// Where the visitor went when a page closed (React has already moved on).
export function exitTo(path = window.location.pathname) {
  if (path.startsWith('/market/cart')) return 'cart';
  if (path.startsWith('/market/checkout')) return 'checkout';
  if (UUID_PATH.test(path)) return 'other_product';
  if (path.startsWith('/market/search') || path.startsWith('/market/c/')) return 'back_to_list';
  return 'other';
}

const num = (v) => (v == null || v === '' || Number.isNaN(Number(v)) ? undefined : Number(v));

// One product page view: dwell, the blocks it reached, the last one seen,
// and whether it ended in an add. `ctx()` returns the page's current numbers.
export function startProductView(productId, props) {
  const opened = Date.now();
  const seen = new Set();
  let lastBlock = null;
  let added = false;
  let ended = false;
  track('product_viewed', { product_id: productId, source: productSource(), ...props });

  const end = (exit) => {
    if (ended) return;
    ended = true;
    document.removeEventListener('visibilitychange', onHide);
    track('product_left', {
      product_id: productId, dwell_ms: Date.now() - opened, max_scroll_pct: screenScrollPct(),
      last_block: lastBlock, added, exit: exit || exitTo(),
    });
  };
  // A closed tab never unmounts the page: count the leave when it hides.
  function onHide() { if (document.visibilityState === 'hidden') end('app_closed'); }
  document.addEventListener('visibilitychange', onHide);

  return {
    end,
    added() { added = true; },
    // A block came into view. The first sighting of each is an event; the last one is remembered.
    blockSeen(name, el) {
      lastBlock = name;
      if (seen.has(name)) return;
      seen.add(name);
      if (name === 'qty_table') {
        track('product_qty_table_seen', { product_id: productId, seconds_after_open: Math.round((Date.now() - opened) / 1000) });
      } else if (name === 'shipping') {
        const cards = Array.from(el?.querySelectorAll('[data-ship-option]') || []);
        const price = (m) => num(cards.find((c) => c.dataset.shipOption === m)?.querySelector('[data-ship-unit]')?.dataset.shipUnit);
        track('shipping_option_viewed', {
          product_id: productId, options: cards.map((c) => c.dataset.shipOption),
          air_price_sar: price('air'), sea_price_sar: price('sea'),
          sea_locked: !!el?.querySelector('[data-ship-locked]'),
        });
      } else if (name === 'save_upsell') {
        track('save_upsell_shown', { product_id: productId, n: num(el?.dataset.shipUnlock), saving_sar: num(el?.dataset.shipSave) });
      }
      if (name !== 'save_upsell') track('product_block_seen', { product_id: productId, block: name });
    },
  };
}

// Watch every [data-growth-block] (and the sea unlock line) inside the page;
// a block counts once half of it is on screen.
export function observeBlocks(view) {
  if (!view || typeof IntersectionObserver === 'undefined') return () => {};
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (!e.isIntersecting || e.intersectionRatio < 0.5) return;
      const name = e.target.dataset.growthBlock || (e.target.dataset.shipUnlock ? 'save_upsell' : null);
      if (name) view.blockSeen(name, e.target);
    });
  }, { threshold: [0.5] });
  document.querySelectorAll('[data-growth-block], [data-ship-unlock]').forEach((el) => io.observe(el));
  return () => io.disconnect();
}

// ── Sign-up / sign-in (step 7) ──────────────────────────────────────────────
// Why a code step failed: the provider's error CODE only (otp_expired,
// over_sms_send_rate_limit…) — never its message, the phone, the email or
// anything typed.
export function otpReason(error) {
  if (!error) return 'no_session';
  const code = error.code || (error.status ? `http_${error.status}` : 'unknown');
  return String(code).replace(/[^a-z0-9_]/gi, '_').slice(0, 60);
}

// What sent the visitor to the sign-in page, from its ?next=.
export function authTrigger(next) {
  if (!next) return 'direct';
  if (next.startsWith('/market/checkout')) return 'checkout';
  if (/^\/(request|requests|idea|managed)/.test(next)) return 'service';
  if (next.startsWith('/rfq/')) return 'rfq_link';
  return 'other';
}
